import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useAnimation,
  useInView,
  useScroll,
  useTransform,
  useSpring,
  MotionValue,
  AnimatePresence,
} from "framer-motion";
import {
  Camera,
  Flame,
  User,
  Trophy,
  Check,
  Sparkles,
  Users,
  Target,
  Zap,
  ChevronRight,
  MessageCircle,
  TrendingUp,
  Star,
  Heart,
  Award,
  Radio,
  Sun,
  Moon,
  Newspaper,
  Home,
  Search,
  Bell,
  ThumbsUp,
  Share2,
  Clock,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useTestMode } from "../contexts/TestModeContext";
import { useAuth } from "../contexts/AuthContext";
import { Header } from "../components/layout/Header";
import { ThemeToggle } from "../components/common/ThemeToggle";
import { Logo } from "../components/common/Logo";

// Enhanced Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const fadeInDown = {
  hidden: { opacity: 0, y: -40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const fadeInScale = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const popIn = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.175, 0.885, 0.32, 1.275] as const,
    },
  },
};

const rotateIn = {
  hidden: { opacity: 0, rotate: -15, scale: 0.8 },
  visible: {
    opacity: 1,
    rotate: 0,
    scale: 1,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const fastStagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.5, rotate: -10 },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const slideInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const slideInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const flipIn = {
  hidden: { opacity: 0, rotateX: -90 },
  visible: {
    opacity: 1,
    rotateX: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
  },
};

// Custom hook for parallax scroll effect
const useParallax = (value: MotionValue<number>, distance: number) => {
  return useTransform(value, [0, 1], [-distance, distance]);
};

// Animated counter component
const AnimatedCounter: React.FC<{ value: string; label: string }> = ({
  value,
  label,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, ease: [0.175, 0.885, 0.32, 1.275] }}
      className="text-center"
    >
      <motion.div
        className="text-3xl font-black text-primary"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 0.2 }}
      >
        {value}
      </motion.div>
      <div className="text-sm text-white/50">{label}</div>
    </motion.div>
  );
};

// Animated section wrapper with configurable margin
const AnimatedSection: React.FC<{
  children: React.ReactNode;
  className?: string;
  margin?: string;
}> = ({ children, className = "", margin = "-100px" }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: margin as any });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Floating particles background
const FloatingParticles: React.FC = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1 h-1 bg-primary/30 rounded-full"
        initial={{
          x:
            Math.random() *
            (typeof window !== "undefined" ? window.innerWidth : 1000),
          y:
            Math.random() *
            (typeof window !== "undefined" ? window.innerHeight : 800),
        }}
        animate={{
          y: [null, Math.random() * -200 - 100],
          opacity: [0, 1, 0],
        }}
        transition={{
          duration: Math.random() * 3 + 4,
          repeat: Infinity,
          delay: Math.random() * 2,
        }}
      />
    ))}
  </div>
);

// Landing Header/Navbar with Theme Toggle (for non-logged in users)
const LandingHeader: React.FC = () => {
  const { scrollY } = useScroll();
  const { isTestMode } = useTestMode();
  const { isDark } = useTheme();

  const headerBgLight = useTransform(
    scrollY,
    [0, 100],
    ["rgba(255,255,255,0.5)", "rgba(255,255,255,0.95)"]
  );
  const headerBgDark = useTransform(
    scrollY,
    [0, 100],
    ["rgba(0,0,0,0.5)", "rgba(0,0,0,0.95)"]
  );

  // Adjust top position when test mode banner is visible
  const topPosition = isTestMode ? "top-[40px]" : "top-0";

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      style={{ backgroundColor: isDark ? headerBgDark : headerBgLight }}
      className={`fixed ${topPosition} left-0 right-0 z-50 backdrop-blur-lg border-b ${
        isDark ? "border-white/5" : "border-black/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <Link to="/" className="flex items-center gap-1.5 group flex-shrink-0">
            {/* Show text-only logo on mobile, full logo on larger screens */}
            <span className="sm:hidden">
              <Logo size="sm" variant="text-only" />
            </span>
            <span className="hidden sm:block">
              <Logo size="sm" showText={true} />
            </span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Theme Toggle */}
            <ThemeToggle size="sm" />

            {/* Show "Join now" on mobile, "Join The Ground" on larger screens */}
            <Link
              to="/login"
              className="px-4 sm:px-6 py-2 sm:py-2.5 bg-primary text-black font-bold rounded-full hover:shadow-glow hover:scale-105 transition-all duration-300 text-xs sm:text-sm whitespace-nowrap"
            >
              <span className="sm:hidden">Join now</span>
              <span className="hidden sm:inline">Join The Ground</span>
            </Link>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

// Hero Section - More energetic with enhanced animations
const HeroSection: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollY } = useScroll();
  const { isDark } = useTheme();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.9]);
  const backgroundY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  return (
    <section
      ref={containerRef}
      className="min-h-screen flex flex-col items-center justify-center px-4 pt-20 pb-16 relative overflow-hidden"
    >
      <FloatingParticles />

      {/* Background image overlay - Stadium crowd with parallax */}
      <motion.div
        className={`absolute inset-0 ${isDark ? "opacity-20" : "opacity-10"}`}
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1495555961986-6d4c1ecb7be3?w=1920&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          y: backgroundY,
        }}
      />
      <div
        className={`absolute inset-0 ${
          isDark
            ? "bg-gradient-to-b from-black via-black/90 to-black"
            : "bg-gradient-to-b from-white via-white/90 to-white"
        }`}
      />

      {/* Radial gradient background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(34,197,94,0.15)_0%,_transparent_70%)]" />

      <motion.div
        style={{ y, opacity, scale }}
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="text-center max-w-4xl mx-auto relative z-10"
      >
        {/* Badge with bounce */}
        <motion.div
          variants={popIn}
          whileHover={{ scale: 1.05 }}
          className={`inline-flex items-center gap-2 px-4 py-2 ${
            isDark ? "bg-white/5" : "bg-black/5"
          } border border-primary/30 rounded-full mb-8`}
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <Radio className="w-4 h-4 text-primary" />
          </motion.div>
          <span
            className={`text-sm ${isDark ? "text-white/80" : "text-black/80"}`}
          >
            Now in Early Access
          </span>
        </motion.div>

        {/* Main Heading with letter animation */}
        <motion.h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.1] mb-6 tracking-tight">
          <motion.span
            variants={fadeInUp}
            className={`${isDark ? "text-white" : "text-black"} inline-block`}
          >
            THE
          </motion.span>{" "}
          <motion.span
            variants={rotateIn}
            className="text-primary relative inline-block"
          >
            PLAYGROUND
            <motion.span
              className="absolute -inset-2 bg-primary/20 blur-2xl -z-10"
              animate={{ opacity: [0.4, 0.8, 0.4], scale: [1, 1.1, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </motion.span>
          <br />
          <motion.span
            variants={fadeInUp}
            className={`${isDark ? "text-white" : "text-black"} inline-block`}
          >
            FOR SPORTS FANS
          </motion.span>
        </motion.h1>

        {/* Subheading */}
        <motion.p
          variants={fadeInUp}
          className={`text-xl sm:text-2xl ${
            isDark ? "text-white/70" : "text-black/70"
          } max-w-2xl mx-auto mb-4 leading-relaxed`}
        >
          Where the real ones sweat together, talk trash, share wins, and build
          their legacy.
        </motion.p>

        {/* Tagline with typing effect feel */}
        <motion.p
          variants={fadeInUp}
          className="text-base text-primary/80 font-medium mb-10"
        >
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            Your bets.
          </motion.span>{" "}
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.5 }}
          >
            Your crew.
          </motion.span>{" "}
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.5 }}
          >
            Your Ground.
          </motion.span>
        </motion.p>

        {/* CTA Buttons with enhanced hover */}
        <motion.div
          variants={fadeInUp}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
            <Link
              to="/login"
              className="group inline-flex items-center gap-3 px-10 py-5 bg-primary text-black font-bold text-lg rounded-full shadow-glow hover:shadow-glow-lg transition-all duration-300"
            >
              GET EARLY ACCESS
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ChevronRight size={22} />
              </motion.div>
            </Link>
          </motion.div>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className={`${isDark ? "text-white/40" : "text-black/40"} text-sm`}
          >
            Free to join. Always.
          </motion.span>
        </motion.div>

        {/* Stats with pop-in animation */}
        <motion.div
          variants={fastStagger}
          className="flex flex-wrap justify-center gap-8 mt-16"
        >
          {[
            { value: "50+", label: "Leagues" },
            { value: "24/7", label: "Live Action" },
            { value: "100%", label: "Fan-First" },
          ].map((stat, i) => (
            <motion.div
              key={i}
              variants={popIn}
              whileHover={{ scale: 1.1, y: -5 }}
              className="text-center cursor-default"
            >
              <motion.div
                className="text-3xl font-black text-primary"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
              >
                {stat.value}
              </motion.div>
              <div
                className={`text-sm ${
                  isDark ? "text-white/50" : "text-black/50"
                }`}
              >
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Scroll indicator with enhanced animation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 0.5 }}
          className="mt-20"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-8 h-12 border-2 border-primary/40 rounded-full mx-auto flex justify-center pt-2 cursor-pointer hover:border-primary/60 transition-colors"
          >
            <motion.div
              animate={{ y: [0, 16, 0], opacity: [1, 0.2, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-2 h-4 bg-primary rounded-full"
            />
          </motion.div>
          <motion.p
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
            className={`text-xs ${
              isDark ? "text-white/30" : "text-black/30"
            } mt-2`}
          >
            Scroll to explore
          </motion.p>
        </motion.div>
      </motion.div>
    </section>
  );
};

// Feature Card Component with Image
interface FeatureCardProps {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  description: string;
  direction?: "left" | "right";
  imageUrl?: string;
  ctaText?: string;
  ctaLink?: string;
  highlights?: string[];
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon: Icon,
  title,
  subtitle,
  description,
  direction = "left",
  imageUrl,
  ctaText,
  ctaLink,
  highlights,
}) => {
  const { isDark } = useTheme();

  return (
    <AnimatedSection className="py-16 sm:py-24 px-4">
      <motion.div
        variants={direction === "left" ? slideInLeft : slideInRight}
        className="max-w-6xl mx-auto"
      >
        <div
          className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-12 ${
            direction === "right" ? "lg:flex-row-reverse" : ""
          }`}
        >
          {/* Image/Visual Side */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="flex-shrink-0 w-full lg:w-1/2 relative"
          >
            <div className="aspect-[4/3] rounded-3xl overflow-hidden relative">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <Icon className="w-24 h-24 text-primary/50" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-primary/20 backdrop-blur-sm flex items-center justify-center">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm font-semibold text-white/80">
                  {subtitle}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Content Side */}
          <div
            className={`flex-1 text-center lg:text-left ${
              direction === "right" ? "lg:text-right" : ""
            }`}
          >
            <motion.div
              variants={fadeInUp}
              className="inline-block px-3 py-1 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-semibold mb-4"
            >
              {subtitle}
            </motion.div>
            <motion.h2
              variants={fadeInUp}
              className={`text-3xl sm:text-4xl lg:text-5xl font-black ${
                isDark ? "text-white" : "text-black"
              } mb-6`}
            >
              {title}
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className={`text-lg ${
                isDark ? "text-white/60" : "text-black/60"
              } leading-relaxed whitespace-pre-line ${
                direction === "right" ? "lg:ml-auto" : ""
              } max-w-lg`}
            >
              {description}
            </motion.p>

            {/* Highlights */}
            {highlights && highlights.length > 0 && (
              <motion.div
                variants={fadeInUp}
                className={`mt-6 space-y-2 ${
                  direction === "right" ? "lg:ml-auto" : ""
                } max-w-lg`}
              >
                {highlights.map((highlight, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-2 ${
                      direction === "right" ? "lg:justify-end" : ""
                    }`}
                  >
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    <span
                      className={`text-sm ${
                        isDark ? "text-white/70" : "text-black/70"
                      }`}
                    >
                      {highlight}
                    </span>
                  </div>
                ))}
              </motion.div>
            )}

            {/* CTA Button */}
            {ctaText && ctaLink && (
              <motion.div
                variants={fadeInUp}
                className={`mt-8 ${
                  direction === "right" ? "lg:ml-auto lg:text-right" : ""
                }`}
              >
                <Link
                  to={ctaLink}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl transition-all hover:scale-105 hover:shadow-lg hover:shadow-primary/30"
                >
                  <Sparkles className="w-5 h-5" />
                  {ctaText}
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatedSection>
  );
};

// SweatRooms Section
const SweatRoomsSection: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <AnimatedSection className="py-20 sm:py-32 px-4 relative overflow-hidden">
      {/* Background image - Sports bar / fans watching */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1522778119026-d647f0596c20?w=1920&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* Background effect */}
      <div
        className={`absolute inset-0 ${
          isDark
            ? "bg-gradient-to-b from-black via-primary/10 to-black"
            : "bg-gradient-to-b from-white via-primary/10 to-white"
        }`}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(34,197,94,0.2)_0%,_transparent_50%)]" />

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 border border-primary/30 rounded-full mb-6">
            <Flame className="w-5 h-5 text-primary animate-pulse" />
            <span className="text-primary font-semibold">SweatRooms</span>
          </div>
          <h2
            className={`text-4xl sm:text-5xl lg:text-6xl font-black ${
              isDark ? "text-white" : "text-black"
            } mb-6`}
          >
            SWEAT THE GAME
            <br />
            <span className="text-primary">TOGETHER</span>
          </h2>
          <p
            className={`text-xl ${
              isDark ? "text-white/60" : "text-black/60"
            } max-w-2xl mx-auto`}
          >
            Join live rooms during games. React in real-time. Feel every play
            with thousands of fans who get it.
          </p>
        </motion.div>

        {/* Room Cards */}
        <motion.div
          variants={fastStagger}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {[
            {
              title: "Public Rooms",
              desc: "Jump into the action with fans worldwide",
              icon: Users,
              live: true,
              image:
                "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=400&h=300&fit=crop",
            },
            {
              title: "Crew Rooms",
              desc: "Private spaces for you and your squad",
              icon: MessageCircle,
              live: false,
              image:
                "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=400&h=300&fit=crop",
            },
            {
              title: "Watch Parties",
              desc: "Sync up for the biggest games of the season",
              icon: Radio,
              live: true,
              image:
                "https://images.unsplash.com/photo-1489944440615-453fc2b6a9a9?w=400&h=300&fit=crop",
            },
          ].map((room, i) => (
            <motion.div
              key={i}
              variants={fadeInScale}
              whileHover={{ scale: 1.03, y: -5 }}
              className={`relative rounded-3xl overflow-hidden ${
                isDark
                  ? "bg-black/50 border-white/10"
                  : "bg-white/50 border-black/10"
              } border hover:border-primary/40 transition-all duration-300 backdrop-blur-sm group`}
            >
              {/* Card Image */}
              <div className="h-40 relative overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5">
                <img
                  src={room.image}
                  alt={room.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    // Fallback to gradient if image fails
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                {room.live && (
                  <div className="absolute top-4 right-4 flex items-center gap-2 px-2 py-1 bg-black/50 rounded-full backdrop-blur-sm">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-xs text-red-400 font-medium">
                      LIVE
                    </span>
                  </div>
                )}
              </div>
              {/* Card Content */}
              <div className="p-6">
                <room.icon className="w-10 h-10 text-primary mb-4 group-hover:scale-110 transition-transform" />
                <h3
                  className={`text-xl font-bold ${
                    isDark ? "text-white" : "text-black"
                  } mb-2`}
                >
                  {room.title}
                </h3>
                <p className={isDark ? "text-white/50" : "text-black/50"}>
                  {room.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// GT Token Section
const GTTokenSection: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <AnimatedSection className="py-20 sm:py-32 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <motion.div
            variants={scaleIn}
            className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 mb-8 relative"
          >
            <Sparkles className="text-primary w-12 h-12" />
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-primary/50"
              animate={{ scale: [1, 1.2, 1], opacity: [1, 0, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className={`text-4xl sm:text-5xl font-black ${
              isDark ? "text-white" : "text-black"
            } mb-4`}
          >
            EARN <span className="text-primary">GROUNDTOKENS</span>
          </motion.h2>

          <motion.p
            variants={fadeInUp}
            className={`text-xl ${
              isDark ? "text-white/60" : "text-black/60"
            } max-w-2xl mx-auto mb-4`}
          >
            Play games. Hit streaks. Show up daily. The more you grind, the more
            you earn.
          </motion.p>

          <motion.p variants={fadeInUp} className="text-primary font-semibold">
            Use GT to unlock gear, tip your crew, and flex your status.
          </motion.p>
        </div>

        {/* Ways to Earn */}
        <motion.div
          variants={fastStagger}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
        >
          {[
            {
              icon: Target,
              label: "Daily Trivia",
              desc: "Test your knowledge",
              gt: "+50 GT",
            },
            {
              icon: Zap,
              label: "Login Streaks",
              desc: "Show up every day",
              gt: "+25 GT",
            },
            {
              icon: TrendingUp,
              label: "Predictions",
              desc: "Call the winners",
              gt: "+100 GT",
            },
            {
              icon: Heart,
              label: "Community",
              desc: "Engage & connect",
              gt: "+10 GT",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              whileHover={{ scale: 1.05, y: -5 }}
              className={`p-6 rounded-2xl ${
                isDark
                  ? "bg-white/5 border-white/10"
                  : "bg-black/5 border-black/10"
              } border hover:border-primary/30 transition-all text-center group`}
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                <item.icon className="text-primary w-7 h-7" />
              </div>
              <h3
                className={`${
                  isDark ? "text-white" : "text-black"
                } font-bold mb-1`}
              >
                {item.label}
              </h3>
              <p
                className={`${
                  isDark ? "text-white/40" : "text-black/40"
                } text-sm mb-3`}
              >
                {item.desc}
              </p>
              <span className="text-primary font-bold text-sm">{item.gt}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Your Identity Section
const IdentitySection: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <AnimatedSection className="py-20 sm:py-32 px-4 relative">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div>
            <motion.div
              variants={fadeInUp}
              className={`inline-flex items-center gap-2 px-4 py-2 ${
                isDark
                  ? "bg-white/5 border-white/10"
                  : "bg-black/5 border-black/10"
              } border rounded-full mb-6`}
            >
              <User className="w-4 h-4 text-primary" />
              <span
                className={`${
                  isDark ? "text-white/70" : "text-black/70"
                } text-sm`}
              >
                Your Sports Identity
              </span>
            </motion.div>

            <motion.h2
              variants={fadeInUp}
              className={`text-4xl sm:text-5xl font-black ${
                isDark ? "text-white" : "text-black"
              } mb-6`}
            >
              BUILD YOUR
              <br />
              <span className="text-primary">LEGACY</span>
            </motion.h2>

            <motion.p
              variants={fadeInUp}
              className={`text-lg ${
                isDark ? "text-white/60" : "text-black/60"
              } mb-8 leading-relaxed`}
            >
              Every pick tracked. Every prediction logged. Every win celebrated.
              Your profile tells your story - from day one hot takes to
              championship calls.
            </motion.p>

            <motion.div variants={fastStagger} className="space-y-4">
              {[
                "Track your pick history & win rate",
                "Earn badges for clutch predictions",
                "Climb the leaderboard with streaks",
                "Build your reputation & rule the Playground",
              ].map((item, i) => (
                <motion.div
                  key={i}
                  variants={slideInLeft}
                  className="flex items-center gap-3"
                >
                  <Check className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className={isDark ? "text-white/70" : "text-black/70"}>
                    {item}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Visual - Profile Screenshot */}
          <motion.div
            variants={slideInRight}
            whileHover={{ scale: 1.02 }}
            className="flex-shrink-0 w-full relative"
          >
            <div className="aspect-[4/3] rounded-3xl overflow-hidden relative">
              <img
                src="/assets/images/profile_home.jpg"
                alt="ParlayGround Profile"
                className="w-full h-full object-cover"
              />
              {/* Gradient overlay at bottom */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

              {/* OG Tag badge overlay */}
              <div className="absolute top-4 right-4">
                <div className="px-4 py-2 bg-primary/90 backdrop-blur-sm rounded-full shadow-lg">
                  <span className="text-black text-sm font-bold">Rookie</span>
                </div>
              </div>

              {/* Bottom label like FeatureCard */}
              <div className="absolute bottom-4 left-4 flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-primary/20 backdrop-blur-sm flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm font-semibold text-white/80">
                  Your Profile
                </span>
              </div>
            </div>

            {/* Floating elements */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute -top-4 -right-4 p-3 rounded-xl bg-primary/20 border border-primary/30"
            >
              <Award className="w-6 h-6 text-primary" />
            </motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2.5, repeat: Infinity }}
              className={`absolute -bottom-4 -left-4 p-3 rounded-xl ${
                isDark
                  ? "bg-white/10 border-white/20"
                  : "bg-black/10 border-black/20"
              } border`}
            >
              <Star
                className={`w-6 h-6 ${isDark ? "text-white" : "text-black"}`}
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Founder's Note Section - with green background wrapper
const FounderSection: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <div className="relative overflow-hidden">
      {/* Green gradient background wrapper */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/20 via-primary/10 to-primary/20" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(34,197,94,0.25)_0%,_transparent_70%)]" />

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

      <AnimatedSection className="py-20 sm:py-32 px-4 relative z-10">
        <div className="max-w-3xl mx-auto">
          <motion.div
            variants={fadeInScale}
            className={`relative p-10 sm:p-14 rounded-3xl ${
              isDark ? "bg-black/60" : "bg-white/60"
            } backdrop-blur-sm border border-primary/30 shadow-[0_0_40px_rgba(34,197,94,0.15)]`}
          >
            {/* Quote mark */}
            <div className="absolute -top-6 left-10 text-8xl text-primary/30 font-serif">
              "
            </div>

            <motion.div variants={fadeInUp} className="relative z-10">
              <div className="text-primary font-bold text-sm uppercase tracking-wider mb-6">
                From The Founder
              </div>

              <div
                className={`space-y-6 text-lg sm:text-xl ${
                  isDark ? "text-white/80" : "text-black/80"
                } leading-relaxed mb-8`}
              >
                <p>
                  I built ParlayGround because sports are the most human thing
                  we collectively obsess over.
                </p>
                <p>
                  Some fans scream, some take it in quietly, and both
                  experiences deserve a real home online.
                </p>
                <p>
                  ParlayGround is my answer: one commons where sports culture
                  finally stops living in fragments and starts living together.
                  It's a place shaped by fans, not controlled by them, because
                  sports culture was never meant to be curated - it was meant to
                  be felt and shared, in any style.
                </p>
                <p
                  className={`${
                    isDark ? "text-white" : "text-black"
                  } font-semibold text-xl`}
                >
                  This is the platform I always wanted to exist.
                </p>
                <p className="text-primary font-bold text-2xl">
                  Welcome to the Ground.
                </p>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/30 flex items-center justify-center border border-primary/40">
                  <span className="text-primary font-bold text-xl">MH</span>
                </div>
                <div>
                  <div
                    className={`${
                      isDark ? "text-white" : "text-black"
                    } font-bold`}
                  >
                    Malcolm Hooper
                  </div>
                  <div className="text-primary text-sm">
                    Founder, ParlayGround
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </AnimatedSection>
    </div>
  );
};

// Simulated App View - Community Feed with Live Slip
type SimStage = "slip" | "sweatroom" | "community" | "news" | "complete";

const stageContent: Record<
  SimStage,
  { title: string; subtitle: string; highlight: string }
> = {
  slip: {
    title: "Your Digital Slip",
    subtitle: "Track every pick in real-time across all sportsbooks",
    highlight: "slip",
  },
  sweatroom: {
    title: "SweatRooms",
    subtitle: "Watch games live with fans who have skin in the game",
    highlight: "sweatroom",
  },
  community: {
    title: "The Community",
    subtitle: "Real reactions. Real fans. Real time.",
    highlight: "community",
  },
  news: {
    title: "Live Updates",
    subtitle: "Breaking news filtered to YOUR picks",
    highlight: "news",
  },
  complete: {
    title: "All Connected",
    subtitle: "Your game. Your community. One place.",
    highlight: "all",
  },
};

type ActiveScreen = "home" | "sweatroom";

const SimulatedAppView: React.FC = () => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState<"news" | "community">("community");
  const [activeScreen, setActiveScreen] = useState<ActiveScreen>("home");
  const [visiblePosts, setVisiblePosts] = useState<number[]>([]);
  const [visibleNews, setVisibleNews] = useState<number[]>([]);
  const [slipStatus, setSlipStatus] = useState<{
    pick1: string;
    pick2: string;
    score1: string;
    score2: string;
  }>({
    pick1: "winning",
    pick2: "pending",
    score1: "24-17",
    score2: "Q2 8:45",
  });
  const [sweatRoomCount, setSweatRoomCount] = useState(847);
  const [gameTime, setGameTime] = useState("Q4 2:34");
  const [likeBumps, setLikeBumps] = useState<Record<number, number>>({});
  const [replyBumps, setReplyBumps] = useState<Record<number, number>>({});
  const [currentStage, setCurrentStage] = useState<SimStage>("slip");
  const [sweatRoomMessages, setSweatRoomMessages] = useState<
    Array<{ id: number; user: string; msg: string }>
  >([]);
  const [chiefScore, setChiefScore] = useState(24);
  const [raidersScore, setRaidersScore] = useState(17);

  // All SweatRoom messages
  const allSweatRoomMessages = [
    { id: 1, user: "ChiefsKingdom", msg: "LET'S GO!! 🔥" },
    { id: 2, user: "GridironGuru", msg: "Mahomes is cooking" },
    { id: 3, user: "BetBoss", msg: "Need this TD!!" },
    { id: 4, user: "ParlayCzar", msg: "TOUCHDOWN!!! 💰💰💰" },
    { id: 5, user: "SweatLord", msg: "Chiefs ML hitting" },
    { id: 6, user: "SlipKing", msg: "What a play!" },
    { id: 7, user: "OGPicker", msg: "Defense is ELITE" },
    { id: 8, user: "ChiefsKingdom", msg: "ONE MORE STOP!" },
  ];

  // All community posts (will animate in)
  const allCommunityPosts = [
    {
      id: 1,
      user: "ChiefsKingdom",
      avatar: "CK",
      time: "just now",
      content: "Chiefs ML looking STRONG! 🔥 Who's sweating with me?",
      likes: 47,
      replies: 12,
      sport: "NFL",
    },
    {
      id: 2,
      user: "HoopsDreams",
      avatar: "HD",
      time: "1m ago",
      content: "Lakers -4.5 was the play. LeBron activated. 👑",
      likes: 89,
      replies: 23,
      sport: "NBA",
    },
    {
      id: 3,
      user: "BronxBomber",
      avatar: "BB",
      time: "2m ago",
      content: "Yankees over 8.5 runs ✅ Judge is on fire!",
      likes: 156,
      replies: 41,
      sport: "MLB",
      isHit: true,
    },
    {
      id: 4,
      user: "GridironGuru",
      avatar: "GG",
      time: "just now",
      content: "TOUCHDOWN CHIEFS! 🏈 This parlay is ALIVE!",
      likes: 234,
      replies: 67,
      sport: "NFL",
      isNew: true,
    },
    {
      id: 5,
      user: "CourtKing",
      avatar: "CK",
      time: "just now",
      content: "AD with the block! Lakers defense is ELITE tonight 🛡️",
      likes: 112,
      replies: 28,
      sport: "NBA",
      isNew: true,
    },
  ];

  // All news items (will animate in)
  const allNewsItems = [
    {
      id: 1,
      headline: "Mahomes leads Chiefs to 4th quarter comeback",
      source: "ESPN",
      time: "LIVE",
      sport: "NFL",
      isLive: true,
    },
    {
      id: 2,
      headline: "Lakers injury update: Davis expected to play tonight",
      source: "The Athletic",
      time: "12m ago",
      sport: "NBA",
    },
    {
      id: 3,
      headline: "Trade deadline: Yankees acquire All-Star closer",
      source: "MLB Network",
      time: "1h ago",
      sport: "MLB",
    },
    {
      id: 4,
      headline: "BREAKING: Chiefs score go-ahead touchdown in Q4",
      source: "NFL Network",
      time: "LIVE",
      sport: "NFL",
      isLive: true,
      isNew: true,
    },
    {
      id: 5,
      headline: "Lakers take 8-point lead entering 4th quarter",
      source: "ESPN",
      time: "LIVE",
      sport: "NBA",
      isLive: true,
      isNew: true,
    },
  ];

  // Stage progression - cycles through all stages
  useEffect(() => {
    const stages: SimStage[] = [
      "slip",
      "sweatroom",
      "community",
      "news",
      "complete",
    ];
    let stageIndex = 0;

    const stageInterval = setInterval(() => {
      stageIndex = (stageIndex + 1) % stages.length;
      const newStage = stages[stageIndex];
      setCurrentStage(newStage);

      // Sync screen and tab with stage
      if (newStage === "sweatroom") {
        setActiveScreen("sweatroom");
      } else {
        setActiveScreen("home");
        if (newStage === "community") {
          setActiveTab("community");
        } else if (newStage === "news") {
          setActiveTab("news");
        }
      }
    }, 4000);

    return () => clearInterval(stageInterval);
  }, []);

  // Animate posts appearing one by one
  useEffect(() => {
    // Start with first 2 posts
    setVisiblePosts([1, 2]);

    const addPost = (index: number, id: number) => {
      setTimeout(() => {
        setVisiblePosts((prev) => {
          if (prev.includes(id)) return prev;
          // Keep max 3 visible, remove oldest
          const newPosts = [...prev, id];
          if (newPosts.length > 3) newPosts.shift();
          return newPosts;
        });
      }, index * 4000 + 2000);
    };

    // Add remaining posts over time
    addPost(1, 3);
    addPost(2, 4);
    addPost(3, 5);

    // Loop: reset and start again
    const loopInterval = setInterval(() => {
      setVisiblePosts([1, 2]);
      addPost(1, 3);
      addPost(2, 4);
      addPost(3, 5);
    }, 20000);

    return () => clearInterval(loopInterval);
  }, []);

  // Animate news appearing one by one
  useEffect(() => {
    // Start with first 2 news
    setVisibleNews([1, 2]);

    const addNews = (index: number, id: number) => {
      setTimeout(() => {
        setVisibleNews((prev) => {
          if (prev.includes(id)) return prev;
          const newNews = [...prev, id];
          if (newNews.length > 3) newNews.shift();
          return newNews;
        });
      }, index * 3500 + 1500);
    };

    addNews(1, 3);
    addNews(2, 4);
    addNews(3, 5);

    // Loop
    const loopInterval = setInterval(() => {
      setVisibleNews([1, 2]);
      addNews(1, 3);
      addNews(2, 4);
      addNews(3, 5);
    }, 18000);

    return () => clearInterval(loopInterval);
  }, []);

  // Animate slip status changes
  useEffect(() => {
    const slipUpdates = [
      {
        pick1: "winning",
        pick2: "pending",
        score1: "24-17",
        score2: "Q2 8:45",
      },
      {
        pick1: "winning",
        pick2: "pending",
        score1: "24-17",
        score2: "Q2 4:22",
      },
      {
        pick1: "winning",
        pick2: "winning",
        score1: "31-17",
        score2: "Q3 11:00",
      },
      {
        pick1: "winning",
        pick2: "winning",
        score1: "31-17",
        score2: "Q3 6:15",
      },
      { pick1: "hit", pick2: "winning", score1: "Final", score2: "Q4 2:30" },
    ];

    let index = 0;
    const updateSlip = () => {
      setSlipStatus(slipUpdates[index]);
      index = (index + 1) % slipUpdates.length;
    };

    const slipInterval = setInterval(updateSlip, 5000);
    return () => clearInterval(slipInterval);
  }, []);

  // Animate sweatroom count
  useEffect(() => {
    const countInterval = setInterval(() => {
      setSweatRoomCount((prev) => prev + Math.floor(Math.random() * 5) + 1);
    }, 2000);
    return () => clearInterval(countInterval);
  }, []);

  // Animate SweatRoom messages
  useEffect(() => {
    let msgIndex = 0;
    const msgInterval = setInterval(() => {
      setSweatRoomMessages((prev) => {
        const newMsg =
          allSweatRoomMessages[msgIndex % allSweatRoomMessages.length];
        msgIndex++;
        const updated = [...prev, { ...newMsg, id: Date.now() }];
        // Keep only last 4 messages
        if (updated.length > 4) updated.shift();
        return updated;
      });
    }, 1800);
    return () => clearInterval(msgInterval);
  }, []);

  // Animate game time
  useEffect(() => {
    const times = [
      "Q4 2:34",
      "Q4 2:18",
      "Q4 1:55",
      "Q4 1:32",
      "Q4 1:10",
      "Q4 0:45",
      "Q4 0:22",
      "FINAL",
    ];
    let index = 0;
    const timeInterval = setInterval(() => {
      setGameTime(times[index]);
      index = (index + 1) % times.length;
    }, 3000);
    return () => clearInterval(timeInterval);
  }, []);

  // Animate scores during sweatroom
  useEffect(() => {
    if (currentStage === "sweatroom") {
      const scoreTimeout = setTimeout(() => {
        setChiefScore(31);
      }, 2000);
      return () => clearTimeout(scoreTimeout);
    } else {
      setChiefScore(24);
      setRaidersScore(17);
    }
  }, [currentStage]);

  // Animate likes and replies incrementing
  useEffect(() => {
    const likeInterval = setInterval(() => {
      const postId = Math.floor(Math.random() * 5) + 1;
      setLikeBumps((prev) => ({
        ...prev,
        [postId]: (prev[postId] || 0) + Math.floor(Math.random() * 3) + 1,
      }));
    }, 1500);

    const replyInterval = setInterval(() => {
      const postId = Math.floor(Math.random() * 5) + 1;
      setReplyBumps((prev) => ({ ...prev, [postId]: (prev[postId] || 0) + 1 }));
    }, 3000);

    return () => {
      clearInterval(likeInterval);
      clearInterval(replyInterval);
    };
  }, []);

  // Filter visible posts/news
  const communityPosts = allCommunityPosts
    .filter((p) => visiblePosts.includes(p.id))
    .map((p) => ({
      ...p,
      likes: p.likes + (likeBumps[p.id] || 0),
      replies: p.replies + (replyBumps[p.id] || 0),
    }));
  const newsItems = allNewsItems.filter((n) => visibleNews.includes(n.id));

  // Dynamic slip data
  const liveSlip = {
    picks: [
      {
        team: "Chiefs",
        line: "ML",
        status: slipStatus.pick1,
        score: slipStatus.score1,
      },
      {
        team: "Lakers",
        line: "-4.5",
        status: slipStatus.pick2,
        score: slipStatus.score2,
      },
    ],
    potential: slipStatus.pick1 === "hit" ? "$450" : "$450",
    odds: "+320",
  };

  // Dynamic SweatRoom data
  const activeSweatRoom = {
    name: "Chiefs vs Raiders",
    participants: sweatRoomCount,
    sport: "NFL",
    status: gameTime,
  };

  return (
    <AnimatedSection className="py-10 sm:py-16 px-4 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(34,197,94,0.1)_0%,_transparent_60%)]" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header - Dynamic based on stage */}
        <motion.div variants={fadeInUp} className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/20 border border-primary/30 rounded-full mb-4">
            <Users size={14} className="text-primary" />
            <span className="text-primary font-semibold text-xs sm:text-sm">
              THE EXPERIENCE
            </span>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStage}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <h2
                className={`text-2xl sm:text-3xl lg:text-4xl font-black mb-2 ${
                  isDark ? "text-white" : "text-black"
                }`}
              >
                {stageContent[currentStage].title === "All Connected" ? (
                  <>
                    ALL <span className="text-primary">CONNECTED</span>
                  </>
                ) : (
                  <span className="text-primary">
                    {stageContent[currentStage].title}
                  </span>
                )}
              </h2>
              <p
                className={`text-sm sm:text-base ${
                  isDark ? "text-white/60" : "text-black/60"
                } max-w-lg mx-auto`}
              >
                {stageContent[currentStage].subtitle}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Stage indicator dots */}
          <div className="flex items-center justify-center gap-2 mt-4">
            {(
              [
                "slip",
                "sweatroom",
                "community",
                "news",
                "complete",
              ] as SimStage[]
            ).map((stage) => (
              <motion.div
                key={stage}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  currentStage === stage
                    ? "w-6 bg-primary"
                    : "w-1.5 bg-white/20"
                }`}
              />
            ))}
          </div>
        </motion.div>

        {/* Main Layout: Phone + Floating Slip */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-8">
          {/* Phone Mockup */}
          <motion.div
            variants={fadeInUp}
            className="relative"
            style={{ maxWidth: "280px", width: "100%" }}
          >
            {/* Glow Effect */}
            <div className="absolute -inset-4 bg-primary/15 blur-2xl rounded-full opacity-60" />

            {/* Phone Frame */}
            <div className="relative rounded-[2.5rem] p-2.5 bg-gray-900 shadow-2xl">
              {/* Phone Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-black rounded-b-xl z-20" />

              {/* Screen */}
              <div
                className="relative rounded-[2rem] overflow-hidden bg-gray-950"
                style={{ aspectRatio: "9/19" }}
              >
                {/* App Content */}
                <div className="absolute inset-0 flex flex-col">
                  {/* Status Bar */}
                  <div className="h-6 bg-black" />

                  {/* App Header with Logo */}
                  <div className="px-2.5 py-1.5 bg-gray-950 border-b border-white/10 flex items-center justify-between">
                    <img
                      src="/assets/images/logo-light-text.png"
                      alt="ParlayGround"
                      className="h-3.5 object-contain"
                    />
                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-green-500/20 rounded-full">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-green-500 text-[8px] font-medium">
                        LIVE
                      </span>
                    </div>
                  </div>

                  {/* Screen Content - Switches between Home and SweatRoom */}
                  <AnimatePresence mode="wait">
                    {activeScreen === "sweatroom" ? (
                      <motion.div
                        key="sweatroom-screen"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="flex-1 flex flex-col overflow-hidden"
                      >
                        {/* SweatRoom Header */}
                        <div className="px-2 py-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border-b border-orange-500/30">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1.5">
                              <Flame size={12} className="text-orange-500" />
                              <span className="text-white text-[10px] font-bold">
                                SWEATROOM
                              </span>
                            </div>
                            <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/30 rounded">
                              <div className="w-1 h-1 bg-red-500 rounded-full animate-pulse" />
                              <span className="text-red-400 text-[8px] font-bold">
                                {gameTime}
                              </span>
                            </div>
                          </div>

                          {/* Scoreboard */}
                          <div className="bg-black/40 rounded-lg p-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                                  <span className="text-white text-[8px] font-black">
                                    KC
                                  </span>
                                </div>
                                <span className="text-white text-[10px] font-medium">
                                  Chiefs
                                </span>
                              </div>
                              <motion.span
                                key={chiefScore}
                                initial={{ scale: 1.3, color: "#22C55E" }}
                                animate={{ scale: 1, color: "#FFFFFF" }}
                                className="text-white text-lg font-black"
                              >
                                {chiefScore}
                              </motion.span>
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center">
                                  <span className="text-white text-[8px] font-black">
                                    LV
                                  </span>
                                </div>
                                <span className="text-white/70 text-[10px] font-medium">
                                  Raiders
                                </span>
                              </div>
                              <span className="text-white/70 text-lg font-black">
                                {raidersScore}
                              </span>
                            </div>
                          </div>

                          {/* Your Pick Status */}
                          <div className="mt-2 flex items-center justify-between px-2 py-1.5 bg-green-500/20 border border-green-500/30 rounded-lg">
                            <div className="flex items-center gap-1.5">
                              <Target size={10} className="text-green-500" />
                              <span className="text-white text-[9px]">
                                Your Pick:{" "}
                                <span className="text-green-500 font-bold">
                                  Chiefs ML
                                </span>
                              </span>
                            </div>
                            <span className="text-green-500 text-[9px] font-bold">
                              WINNING
                            </span>
                          </div>
                        </div>

                        {/* Live Chat */}
                        <div className="flex-1 flex flex-col overflow-hidden">
                          <div className="px-2 py-1.5 border-b border-white/10 flex items-center justify-between">
                            <span className="text-white/60 text-[9px]">
                              Live Chat
                            </span>
                            <motion.span
                              key={sweatRoomCount}
                              initial={{ scale: 1.1 }}
                              animate={{ scale: 1 }}
                              className="text-orange-400 text-[9px] font-medium"
                            >
                              {sweatRoomCount} sweating
                            </motion.span>
                          </div>
                          <div className="flex-1 overflow-hidden px-2 py-1.5 space-y-1">
                            <AnimatePresence>
                              {sweatRoomMessages.slice(-5).map((msg) => (
                                <motion.div
                                  key={msg.id}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0 }}
                                  className="flex items-start gap-1.5 p-1.5 bg-white/5 rounded-lg"
                                >
                                  <div className="w-5 h-5 rounded-full bg-orange-500/30 flex items-center justify-center flex-shrink-0">
                                    <span className="text-orange-400 text-[7px] font-bold">
                                      {msg.user.substring(0, 2).toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="min-w-0">
                                    <span className="text-orange-400 text-[8px] font-semibold">
                                      {msg.user}
                                    </span>
                                    <p className="text-white/80 text-[9px] leading-tight">
                                      {msg.msg}
                                    </p>
                                  </div>
                                </motion.div>
                              ))}
                            </AnimatePresence>
                          </div>

                          {/* Chat Input */}
                          <div className="px-2 py-1.5 border-t border-white/10">
                            <div className="flex items-center gap-2 px-2 py-1.5 bg-white/5 rounded-lg">
                              <span className="text-white/30 text-[9px] flex-1">
                                Send a message...
                              </span>
                              <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                                <ChevronRight
                                  size={10}
                                  className="text-black"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="home-screen"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="flex-1 flex flex-col overflow-hidden"
                      >
                        {/* Top Tab Bar */}
                        <div className="flex items-center bg-gray-900/80 backdrop-blur-sm border-b border-white/10">
                          <button
                            onClick={() => setActiveTab("community")}
                            className={`flex-1 py-2.5 text-xs font-semibold transition-colors relative ${
                              activeTab === "community"
                                ? "text-primary"
                                : "text-white/50"
                            }`}
                          >
                            <div className="flex items-center justify-center gap-1.5">
                              <Users size={12} />
                              Community
                            </div>
                            {activeTab === "community" && (
                              <motion.div
                                layoutId="activeTab"
                                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                              />
                            )}
                          </button>
                          <button
                            onClick={() => setActiveTab("news")}
                            className={`flex-1 py-2.5 text-xs font-semibold transition-colors relative ${
                              activeTab === "news"
                                ? "text-primary"
                                : "text-white/50"
                            }`}
                          >
                            <div className="flex items-center justify-center gap-1.5">
                              <Newspaper size={12} />
                              News
                            </div>
                            {activeTab === "news" && (
                              <motion.div
                                layoutId="activeTab"
                                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                              />
                            )}
                          </button>
                        </div>

                        {/* SweatRoom Banner */}
                        <motion.div
                          animate={
                            currentStage === "sweatroom"
                              ? { scale: [1, 1.01, 1] }
                              : {}
                          }
                          transition={{
                            duration: 0.5,
                            repeat: currentStage === "sweatroom" ? Infinity : 0,
                          }}
                          className={`mx-2 mt-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-lg transition-all duration-300 overflow-hidden ${
                            currentStage === "sweatroom"
                              ? "border-2 border-orange-500 shadow-lg shadow-orange-500/30"
                              : "border border-orange-500/30"
                          }`}
                        >
                          {/* Header */}
                          <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Flame
                                size={10}
                                className="text-orange-500 flex-shrink-0"
                              />
                              <span className="text-white text-[9px] font-bold truncate">
                                {activeSweatRoom.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/30 rounded">
                                <div className="w-1 h-1 bg-red-500 rounded-full animate-pulse" />
                                <span className="text-red-400 text-[8px] font-medium">
                                  {activeSweatRoom.status}
                                </span>
                              </div>
                              <motion.span
                                key={sweatRoomCount}
                                initial={{ scale: 1.1 }}
                                animate={{ scale: 1 }}
                                className="text-white/40 text-[8px]"
                              >
                                {activeSweatRoom.participants}
                              </motion.span>
                            </div>
                          </div>

                          {/* Live Chat Messages */}
                          <AnimatePresence>
                            {currentStage === "sweatroom" && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="border-t border-orange-500/20 px-2 py-1.5 space-y-1 max-h-[60px] overflow-hidden"
                              >
                                {sweatRoomMessages.slice(-3).map((msg) => (
                                  <motion.div
                                    key={msg.id}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="flex items-center gap-1.5"
                                  >
                                    <span className="text-orange-400 text-[8px] font-semibold">
                                      {msg.user}:
                                    </span>
                                    <span className="text-white/70 text-[8px] truncate">
                                      {msg.msg}
                                    </span>
                                  </motion.div>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>

                        {/* Feed Content */}
                        <div
                          className={`flex-1 overflow-hidden px-2 py-2 rounded-lg transition-all duration-300 ${
                            currentStage === "community" ||
                            currentStage === "news"
                              ? "ring-2 ring-primary/50 shadow-lg shadow-primary/20"
                              : ""
                          }`}
                        >
                          <AnimatePresence mode="wait">
                            {activeTab === "community" ? (
                              <motion.div
                                key="community"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 10 }}
                                className="space-y-2"
                              >
                                {communityPosts.map((post, i) => (
                                  <motion.div
                                    key={post.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    className={`p-2.5 rounded-xl border ${
                                      post.isHit
                                        ? "bg-green-500/10 border-green-500/30"
                                        : "bg-white/5 border-white/10"
                                    }`}
                                  >
                                    <div className="flex items-start gap-2">
                                      <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                                        <span className="text-primary text-[9px] font-bold">
                                          {post.avatar}
                                        </span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-white text-[10px] font-semibold truncate">
                                            {post.user}
                                          </span>
                                          <span className="text-white/30 text-[9px]">
                                            •
                                          </span>
                                          <span className="text-white/40 text-[9px]">
                                            {post.time}
                                          </span>
                                          <span className="px-1.5 py-0.5 bg-white/10 rounded text-[8px] text-white/60">
                                            {post.sport}
                                          </span>
                                        </div>
                                        <p className="text-white/80 text-[10px] mt-0.5 leading-relaxed">
                                          {post.content}
                                        </p>
                                        <div className="flex items-center gap-3 mt-1.5">
                                          <div className="flex items-center gap-1 text-white/40">
                                            <ThumbsUp size={9} />
                                            <span className="text-[9px]">
                                              {post.likes}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-1 text-white/40">
                                            <MessageCircle size={9} />
                                            <span className="text-[9px]">
                                              {post.replies}
                                            </span>
                                          </div>
                                          <Share2
                                            size={9}
                                            className="text-white/40"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                              </motion.div>
                            ) : (
                              <motion.div
                                key="news"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                className="space-y-2"
                              >
                                {newsItems.map((item, i) => (
                                  <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    className={`p-2.5 rounded-xl border ${
                                      item.isLive
                                        ? "bg-red-500/10 border-red-500/30"
                                        : "bg-white/5 border-white/10"
                                    }`}
                                  >
                                    <div className="flex items-center gap-1.5 mb-1">
                                      {item.isLive ? (
                                        <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/30 rounded-full">
                                          <div className="w-1 h-1 bg-red-500 rounded-full animate-pulse" />
                                          <span className="text-red-400 text-[9px] font-bold">
                                            LIVE
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-1 text-white/40">
                                          <Clock size={9} />
                                          <span className="text-[9px]">
                                            {item.time}
                                          </span>
                                        </div>
                                      )}
                                      <span className="px-1.5 py-0.5 bg-white/10 rounded text-[8px] text-white/60">
                                        {item.sport}
                                      </span>
                                    </div>
                                    <p className="text-white text-[10px] font-medium leading-snug">
                                      {item.headline}
                                    </p>
                                    <p className="text-white/40 text-[9px] mt-1">
                                      {item.source}
                                    </p>
                                  </motion.div>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Bottom Navigation */}
                  <div className="bg-gray-900/95 backdrop-blur-sm border-t border-white/10 px-3 py-1.5 pb-3">
                    <div className="flex items-center justify-around">
                      {[
                        { icon: Home, label: "Home", key: "home" },
                        { icon: Search, label: "Explore", key: "explore" },
                        { icon: Target, label: "Picks", key: "picks" },
                        { icon: Flame, label: "Sweat", key: "sweatroom" },
                        { icon: User, label: "Profile", key: "profile" },
                      ].map((item) => {
                        const isActive =
                          (activeScreen === "home" && item.key === "home") ||
                          (activeScreen === "sweatroom" &&
                            item.key === "sweatroom");
                        return (
                          <div
                            key={item.key}
                            className={`flex flex-col items-center gap-0.5 transition-colors ${
                              isActive
                                ? item.key === "sweatroom"
                                  ? "text-orange-500"
                                  : "text-primary"
                                : "text-white/40"
                            }`}
                          >
                            <item.icon size={14} />
                            <span className="text-[7px] font-medium">
                              {item.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Floating Digital Slip - visible on all screens */}
          <motion.div
            variants={slideInRight}
            className="w-full max-w-[280px] sm:max-w-[240px] mt-6 lg:mt-0"
          >
            <motion.div
              animate={
                currentStage === "slip"
                  ? { scale: [1, 1.02, 1] }
                  : { y: [0, -5, 0] }
              }
              transition={{
                duration: currentStage === "slip" ? 0.5 : 3,
                repeat: Infinity,
              }}
              className="relative"
            >
              {/* Glow */}
              <div
                className={`absolute -inset-4 blur-2xl rounded-2xl transition-all duration-300 ${
                  currentStage === "slip" ? "bg-primary/40" : "bg-primary/20"
                }`}
              />

              {/* Slip Card */}
              <div
                className={`relative p-3 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-950 shadow-xl transition-all duration-300 ${
                  currentStage === "slip"
                    ? "border-2 border-primary shadow-lg shadow-primary/30"
                    : "border border-primary/30"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                      <Target size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-white text-xs font-bold">
                        Your Live Slip
                      </p>
                      <p className="text-white/50 text-[10px]">2-Leg Parlay</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 bg-green-500/20 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-green-500 text-[10px] font-medium">
                      LIVE
                    </span>
                  </div>
                </div>

                {/* Picks */}
                <div className="space-y-2 mb-3">
                  {liveSlip.picks.map((pick, i) => (
                    <motion.div
                      key={i}
                      animate={
                        pick.status === "hit" ? { scale: [1, 1.02, 1] } : {}
                      }
                      transition={{ duration: 0.3 }}
                      className={`p-2.5 rounded-lg transition-all ${
                        pick.status === "hit"
                          ? "bg-green-500/20 border border-green-500/50"
                          : pick.status === "winning"
                          ? "bg-green-500/10 border border-green-500/30"
                          : "bg-white/5 border border-white/10"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {pick.status === "hit" && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center"
                            >
                              <Check size={10} className="text-black" />
                            </motion.div>
                          )}
                          <p className="text-white text-xs font-semibold">
                            {pick.team} {pick.line}
                          </p>
                        </div>
                        <div className="text-right">
                          {pick.status === "hit" ? (
                            <motion.p
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="text-green-500 text-[10px] font-black"
                            >
                              HIT! ✓
                            </motion.p>
                          ) : (
                            <>
                              <p
                                className={`text-[10px] font-bold ${
                                  pick.status === "winning"
                                    ? "text-green-500"
                                    : "text-white/60"
                                }`}
                              >
                                {pick.score}
                              </p>
                              {pick.status === "winning" && (
                                <p className="text-green-500/70 text-[9px]">
                                  Winning
                                </p>
                              )}
                              {pick.status === "pending" && (
                                <p className="text-yellow-500/70 text-[9px]">
                                  Live
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Payout */}
                <div className="flex items-center justify-between pt-2 border-t border-white/10">
                  <div>
                    <p className="text-white/50 text-[10px]">Potential</p>
                    <p className="text-primary text-lg font-black">
                      {liveSlip.potential}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/50 text-[10px]">Odds</p>
                    <p className="text-white text-sm font-bold">
                      {liveSlip.odds}
                    </p>
                  </div>
                </div>

                {/* SweatRoom Link */}
                <button className="w-full mt-2 py-1.5 bg-primary/20 hover:bg-primary/30 border border-primary/30 rounded-lg transition-colors">
                  <div className="flex items-center justify-center gap-1.5">
                    <Flame size={12} className="text-primary" />
                    <span className="text-primary text-[10px] font-semibold">
                      SweatRoom
                    </span>
                    <span className="text-primary/50 text-[9px]">•</span>
                    <motion.span
                      key={sweatRoomCount}
                      initial={{ scale: 1.1 }}
                      animate={{ scale: 1 }}
                      className="text-primary/60 text-[9px]"
                    >
                      {sweatRoomCount}
                    </motion.span>
                  </div>
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div variants={fadeInUp} className="text-center mt-6 sm:mt-8">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-black font-bold rounded-full hover:scale-105 transition-transform shadow-lg shadow-primary/30 text-sm sm:text-base"
          >
            <Sparkles size={18} />
            Join The Ground
            <ChevronRight size={18} />
          </Link>
          <p
            className={`mt-2 text-xs sm:text-sm ${
              isDark ? "text-white/40" : "text-black/40"
            }`}
          >
            Free forever. No credit card required.
          </p>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Community Section - The 4th Pillar
const CommunitySection: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <AnimatedSection className="py-20 sm:py-32 px-4 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-primary/5" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(34,197,94,0.1)_0%,_transparent_60%)]" />

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 border border-primary/30 rounded-full mb-6">
            <Users className="w-5 h-5 text-primary" />
            <span className="text-primary font-semibold">Community Hub</span>
          </div>
          <h2
            className={`text-4xl sm:text-5xl lg:text-6xl font-black ${
              isDark ? "text-white" : "text-black"
            } mb-6`}
          >
            SPORTS CULTURE
            <br />
            <span className="text-primary">LIVES HERE</span>
          </h2>
          <p
            className={`text-xl ${
              isDark ? "text-white/60" : "text-black/60"
            } max-w-2xl mx-auto`}
          >
            The conversations before kickoff. The hype building up. News,
            broadcasts, injury updates, trades - all filtered to YOUR teams and
            leagues.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={fastStagger}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {[
            {
              icon: MessageCircle,
              title: "Pre-Game Talk",
              desc: "Build the hype before every game with your community",
              color: "from-blue-500/20 to-blue-600/10",
            },
            {
              icon: Radio,
              title: "Live Updates",
              desc: "News, injuries, trades - filtered to your preferences",
              color: "from-orange-500/20 to-red-500/10",
            },
            {
              icon: TrendingUp,
              title: "Post-Game",
              desc: "Break down what happened with fans who get it",
              color: "from-purple-500/20 to-pink-500/10",
            },
            {
              icon: Heart,
              title: "Your Teams",
              desc: "Personalized feed based on your favorite teams",
              color: "from-primary/20 to-emerald-500/10",
            },
          ].map((feature, i) => (
            <motion.div
              key={i}
              variants={fadeInScale}
              whileHover={{ scale: 1.03, y: -5 }}
              className={`p-6 rounded-2xl bg-gradient-to-br ${
                feature.color
              } border ${
                isDark ? "border-white/10" : "border-black/10"
              } backdrop-blur-sm`}
            >
              <feature.icon className="w-10 h-10 text-primary mb-4" />
              <h3
                className={`text-lg font-bold ${
                  isDark ? "text-white" : "text-black"
                } mb-2`}
              >
                {feature.title}
              </h3>
              <p className={isDark ? "text-white/60" : "text-black/60"}>
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Tagline */}
        <motion.p
          variants={fadeInUp}
          className={`text-center mt-12 text-lg ${
            isDark ? "text-white/50" : "text-black/50"
          }`}
        >
          Sports culture was never meant to be curated - it was meant to be{" "}
          <span className="text-primary font-semibold">felt and shared</span>.
        </motion.p>
      </div>
    </AnimatedSection>
  );
};

// Values Section
const ValuesSection: React.FC = () => {
  const { isDark } = useTheme();
  const values = [
    { text: "Community over clout", icon: Users },
    { text: "Fans over algorithms", icon: Heart },
    { text: "Real moments over highlights", icon: Flame },
    { text: "Togetherness over isolation", icon: MessageCircle },
  ];

  return (
    <AnimatedSection className="py-16 sm:py-24 px-4 relative overflow-hidden">
      {/* Background image - Sports fans celebrating */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1471295253337-3ceaaedca402?w=1920&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        className={`absolute inset-0 ${
          isDark
            ? "bg-gradient-to-b from-black via-black/95 to-black"
            : "bg-gradient-to-b from-white via-white/95 to-white"
        }`}
      />

      <div className="max-w-4xl mx-auto text-center relative z-10">
        <motion.h2
          variants={fadeInUp}
          className="text-4xl sm:text-5xl font-black mb-4"
        >
          <span className={isDark ? "text-white" : "text-black"}>
            THE GROUND
          </span>
          <br />
          <span className="text-primary">STANDS FOR</span>
        </motion.h2>

        <motion.p
          variants={fadeInUp}
          className={isDark ? "text-white/50" : "text-black/50"}
          style={{ marginBottom: "2.5rem" }}
        >
          Our community principles. Non-negotiable.
        </motion.p>

        <motion.div
          variants={fastStagger}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          {values.map((value, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              whileHover={{ scale: 1.02, x: 5 }}
              className={`flex items-center gap-4 p-5 rounded-2xl ${
                isDark
                  ? "bg-white/5 border-white/10"
                  : "bg-black/5 border-black/10"
              } border hover:border-primary/30 transition-all text-left backdrop-blur-sm`}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <value.icon className="w-6 h-6 text-primary" />
              </div>
              <span
                className={`text-lg sm:text-xl ${
                  isDark ? "text-white" : "text-black"
                } font-semibold`}
              >
                {value.text}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Community Disclaimer Section
const DisclaimerSection: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <section className="py-20 sm:py-32 px-4 relative">
      {/* Gradient background to stand out */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-purple-900/20 to-primary/10" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(34,197,94,0.15)_0%,_transparent_70%)]" />

      {/* Subtle pattern overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2322c55e' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Top and bottom borders */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

      <div className="max-w-4xl mx-auto text-center relative z-10">
        <div className="inline-block p-1 rounded-full bg-gradient-to-r from-primary via-white/30 to-primary mb-8">
          <div
            className={`px-6 py-2 ${
              isDark ? "bg-black/80" : "bg-white/80"
            } backdrop-blur-sm rounded-full`}
          >
            <span
              className={`${
                isDark ? "text-white" : "text-black"
              } font-medium text-sm`}
            >
              Important Note
            </span>
          </div>
        </div>

        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-8">
          <span className={isDark ? "text-white" : "text-black"}>NOT A </span>
          <span className="text-red-400 drop-shadow-[0_0_20px_rgba(248,113,113,0.5)]">
            SPORTSBOOK
          </span>
          <br />
          <span className={isDark ? "text-white" : "text-black"}>WE'RE A </span>
          <span className="text-primary drop-shadow-[0_0_20px_rgba(34,197,94,0.5)]">
            COMMUNITY
          </span>
        </h2>

        <p
          className={`text-lg ${
            isDark ? "text-white/70" : "text-black/70"
          } max-w-2xl mx-auto leading-relaxed`}
        >
          No picks to sell. No bets to push. No juice to collect. ParlayGround
          is a free community where real fans connect over the games they love.
          We're building culture - not a casino.
        </p>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <AnimatedSection className="py-24 sm:py-40 px-4 relative overflow-hidden">
      {/* Background image - Stadium lights */}
      <div
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1518091043644-c1d4457512c6?w=1920&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* Background effects */}
      <div
        className={`absolute inset-0 ${
          isDark
            ? "bg-gradient-to-t from-black via-black/80 to-black"
            : "bg-gradient-to-t from-white via-white/80 to-white"
        }`}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(34,197,94,0.2)_0%,_transparent_60%)]" />

      <div className="max-w-3xl mx-auto text-center relative z-10">
        <motion.div variants={scaleIn} className="mb-8">
          <motion.div
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="w-20 h-20 mx-auto rounded-full border-2 border-dashed border-primary/30 flex items-center justify-center"
          >
            <Sparkles className="w-8 h-8 text-primary" />
          </motion.div>
        </motion.div>

        <motion.h2
          variants={fadeInUp}
          className={`text-4xl sm:text-5xl lg:text-6xl font-black ${
            isDark ? "text-white" : "text-black"
          } mb-6`}
        >
          READY TO
          <br />
          <span className="text-primary">HIT THE GROUND?</span>
        </motion.h2>

        <motion.p
          variants={fadeInUp}
          className={`text-xl ${
            isDark ? "text-white/60" : "text-black/60"
          } mb-4`}
        >
          Join thousands of fans building the future of sports community.
        </motion.p>

        <motion.p
          variants={fadeInUp}
          className="text-primary font-semibold mb-10"
        >
          Early members get 100 GT bonus, unique frame & OG status forever.
        </motion.p>

        <motion.div variants={fadeInUp}>
          <Link
            to="/login"
            className="group inline-flex items-center gap-3 px-12 py-6 bg-primary text-black font-black text-xl rounded-full shadow-glow-lg hover:shadow-[0_0_60px_rgba(34,197,94,0.6)] transition-all duration-300 hover:scale-105"
          >
            CLAIM YOUR SPOT
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ChevronRight size={26} />
            </motion.div>
          </Link>
        </motion.div>

        <motion.p
          variants={fadeInUp}
          className={`mt-8 ${
            isDark ? "text-white/30" : "text-black/30"
          } text-sm`}
        >
          Free forever. No credit card required.
        </motion.p>
      </div>
    </AnimatedSection>
  );
};

// Footer
const LandingFooter: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <footer
      className={`py-12 px-4 border-t ${
        isDark ? "border-white/10" : "border-black/10"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center gap-6">
          <Link to="/" className="flex items-center gap-2">
            <Logo size="sm" showText={true} />
          </Link>

          <p
            className={`${
              isDark ? "text-white/40" : "text-black/40"
            } text-center max-w-md`}
          >
            The playground for sports fans. Where every game matters more
            because you're never watching alone.
          </p>

          <div className="flex items-center gap-4 text-sm">
            <Link
              to="/terms"
              className={`${
                isDark
                  ? "text-white/40 hover:text-white"
                  : "text-black/40 hover:text-black"
              } transition-colors`}
            >
              Terms of Service
            </Link>
            <span className={isDark ? "text-white/20" : "text-black/20"}>
              •
            </span>
            <Link
              to="/privacy"
              className={`${
                isDark
                  ? "text-white/40 hover:text-white"
                  : "text-black/40 hover:text-black"
              } transition-colors`}
            >
              Privacy Policy
            </Link>
          </div>

          <div
            className={`flex items-center gap-6 ${
              isDark ? "text-white/30" : "text-black/30"
            } text-sm`}
          >
            <span>© {new Date().getFullYear()} ParlayGround</span>
            <span>•</span>
            <span>Made for fans, by fans</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
export const LandingPage: React.FC = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();

  return (
    <div
      className={`min-h-screen ${
        isDark ? "bg-black text-white" : "bg-white text-black"
      } overflow-x-hidden transition-colors duration-300`}
    >
      {/* Background */}
      <div
        className={`fixed inset-0 ${
          isDark
            ? "bg-[radial-gradient(ellipse_at_top,_#0a0a0a_0%,_#000000_100%)]"
            : "bg-[radial-gradient(ellipse_at_top,_#f8fafc_0%,_#ffffff_100%)]"
        } pointer-events-none transition-colors duration-300`}
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Show Header when logged in, LandingHeader when not */}
        {user ? <Header /> : <LandingHeader />}

        <main>
          <HeroSection />

          {/* Feature Cards */}
          <FeatureCard
            icon={Camera}
            title="ONE SCREENSHOT"
            subtitle="Unified Bet Tracker"
            description={`Privately track your bets, performance metrics, and live SweatRoom stats in real time - unified across ALL sportsbooks. \n\nDrop your picks on Explore where the community reacts, tips, and tails. Every slip shared enters you into monthly draws for sports prizes and fan experiences.`}
            direction="left"
            imageUrl="/assets/images/picks_home.jpg"
            highlights={[
              "Track bets from ANY sportsbook in one dashboard",
              "Earn reputation, reactions & tips on Explore",
              "Monthly prize draws just for sharing picks",
              "Your picks → SweatRooms → Live together",
            ]}
            ctaText="See the Demo"
            ctaLink="/demo"
          />

          <SweatRoomsSection />

          <FeatureCard
            icon={Trophy}
            title="DAILY GAMES"
            subtitle="Mini Games"
            description="Trivia battles. Prediction contests. Streak challenges. Compete against the community every single day for GroundTokens and bragging rights. Your sports IQ finally pays off."
            direction="right"
            imageUrl="https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800&q=80"
          />

          <IdentitySection />

          <GTTokenSection />

          <SimulatedAppView />

          <CommunitySection />

          <FounderSection />

          <ValuesSection />

          <DisclaimerSection />

          <FinalCTASection />
        </main>

        <LandingFooter />
      </div>
    </div>
  );
};

export default LandingPage;
